import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ClipboardList, CalendarClock, Target, Landmark, Users, LogOut, KeyRound, ListTodo, Stethoscope, Sun, Moon, CalendarRange, CalendarDays } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import ThemeSettings from "@/components/ThemeSettings";
import SidebarGreeting from "@/components/SidebarGreeting";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/facilities", label: "Facilities", icon: Building2, testId: "nav-facilities" },
  { to: "/visits", label: "Visits", icon: ClipboardList, testId: "nav-visits" },
  { to: "/planning", label: "Planning", icon: CalendarRange, testId: "nav-planning" },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, testId: "nav-followups" },
  { to: "/tasks", label: "Tasks", icon: ListTodo, testId: "nav-tasks" },
  { to: "/providers", label: "Providers", icon: Stethoscope, testId: "nav-providers" },
  { to: "/swots", label: "SWOT", icon: Target, testId: "nav-swots" },
  { to: "/owners", label: "Corporate Owners", icon: Landmark, testId: "nav-owners" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggleMode } = useTheme();
  const nav = useNavigate();
  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isDark = theme?.mode === "dark";

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-shell">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-slate-200 bg-white md:sticky md:top-0 md:h-screen">
        <div className="px-6 py-6 border-b border-slate-200">
          <Logo size="sm" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex mt-2 ml-11">FL · SNF & ALF</div>
        </div>
        <SidebarGreeting className="border-b border-slate-200" />
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/users"
              data-testid="nav-users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Users className="w-4 h-4" /> Users
            </NavLink>
          )}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              {user?.picture ? <AvatarImage src={user.picture} /> : null}
              <AvatarFallback className="text-xs bg-slate-200">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-500 font-mono-plex uppercase">{user?.role}</div>
            </div>
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggleMode} data-testid="theme-mode-toggle" className="hover:bg-slate-100" title={isDark ? "Switch to light" : "Switch to dark"}>
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <ThemeSettings />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu" className="hover:bg-slate-100">
                  <KeyRound className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem data-testid="menu-change-password" onClick={() => nav("/settings/password")}>
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/login"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 py-3">
        <Logo size="sm" />
        <div className="flex items-center gap-1">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-greeting-toggle" className="hover:bg-slate-100" title="Today & calendar">
                <CalendarDays className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] p-0 overflow-y-auto" data-testid="mobile-greeting-sheet">
              <SheetHeader className="px-4 pt-4 pb-0"><SheetTitle className="sr-only">Today & calendar</SheetTitle></SheetHeader>
              <SidebarGreeting />
            </SheetContent>
          </Sheet>
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={toggleMode} data-testid="theme-mode-toggle-mobile" className="hover:bg-slate-100">
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <ThemeSettings />
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="btn-logout-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <main className="flex-1 md:pl-0 pt-14 md:pt-0 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-6">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to} to={to}
            className={({ isActive }) => `flex flex-col items-center justify-center py-2 text-[10px] ${isActive ? "text-slate-900" : "text-slate-500"}`}
          >
            <Icon className="w-4 h-4 mb-0.5" />
            {label.split(" ")[0]}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
